import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { GroupType } from '../../../entities/groups';
import { ClaimLimitPeriod, IncentiveVisibility } from '../../../entities/incentives';

export const useSchema = () => {
	const { t } = useTranslation('incentives', { keyPrefix: 'form' });

	return (
		z
			.object({
				name: z.string().trim().min(1, t('errorNameTooShort')).max(40, t('errorNameTooLong')),
				icon: z.any().nullable(),
				description: z.string().trim().min(1, t('errorDescriptionRequired')).max(500, t('errorDescriptionRequired')),
				requireProof: z.boolean(),
				conditionsOperator: z.nativeEnum(GroupType),
				conditions: z.array(
					z.object({
						attr: z.string().optional(),
						value: z.string().optional(),
					})
				),
				claimLimitQuantity: z.coerce.number().min(1).max(20_000).nullable(),
				periodType: z.nativeEnum(ClaimLimitPeriod).nullable(),
				userClaimLimit: z.coerce.number().min(1).max(1000).nullable(),
				// This is validated with a refinement below
				amount: z.any(),
				message: z.string().trim().min(1).max(1000, t('errorMessageTooLong')),
				visibility: z.nativeEnum(IncentiveVisibility),
				requireApproval: z.boolean(),
				approvalLevels: z.array(
					z.array(
						z.object({
							id: z.string(),
						})
					)
				),
			})
			// Ensure the amount is an integer
			.refine((data) => typeof data.amount === 'number' && data.amount > 0 && data.amount % 1 === 0, {
				message: t('errorInvalidAmount'),
				path: ['amount'],
			})
			.refine((data) => !/@recipient/.test(data.message) && !/\+\d+/.test(data.message) && !/\+amount/.test(data.message), {
				message: t('errorAmountIncludesTokens'),
				path: ['message'],
			})
			.refine((data) => !data.conditions.length || data.conditions.every((cond) => cond.attr && cond.value), {
				message: t('errorInvalidConditions'),
				path: ['conditions'],
			})
			.refine((data) => !data.requireApproval || data.approvalLevels.every((level) => level.length > 0), {
				message: t('errorInvalidApprovals'),
				path: ['approvalLevels'],
			})
	);
};
